/**
 * Theme Storage Utilities
 * Manages localStorage for widget theme parameters
 */

const STORAGE_KEYS = {
  THEME_ID: "bc_themeId",
  EDIT_MODE: "bc_editMode",
  EDIT_THEME_TOKEN: "bc_editThemeToken",
  PREVIEW_MODE: "bc_previewMode",
  APP_ID: "bc_appId",
  SCOPE: "bc_scope",
  VERSION: "bc_version",
} as const;

export interface ThemeStorageParams {
  themeId: string | null;
  editMode: boolean;
  editThemeToken: string | null;
  previewMode: boolean;
  appId: string | null;
  scope: string | null;
  /** V2 theme version label (e.g. `v2`). Required for Edit live writes. */
  version: string | null;
}

/**
 * Save theme parameters to localStorage. V1 callers (passing just
 * `themeId`) still work — `appId` / `scope` / `version` are optional and
 * only populated by the V2 edit flow.
 */
export const saveThemeParams = (
  themeId: string | null,
  editMode: boolean,
  editThemeToken: string | null,
  previewMode?: boolean,
  appId?: string | null,
  scope?: string | null,
  version?: string | null
): void => {
  try {
    if (themeId) {
      localStorage.setItem(STORAGE_KEYS.THEME_ID, themeId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.THEME_ID);
    }

    localStorage.setItem(STORAGE_KEYS.EDIT_MODE, String(editMode));

    if (editThemeToken) {
      localStorage.setItem(STORAGE_KEYS.EDIT_THEME_TOKEN, editThemeToken);
    } else {
      localStorage.removeItem(STORAGE_KEYS.EDIT_THEME_TOKEN);
    }

    localStorage.setItem(STORAGE_KEYS.PREVIEW_MODE, String(!!previewMode));

    if (appId) {
      localStorage.setItem(STORAGE_KEYS.APP_ID, appId);
    } else {
      localStorage.removeItem(STORAGE_KEYS.APP_ID);
    }
    if (scope) {
      localStorage.setItem(STORAGE_KEYS.SCOPE, scope);
    } else {
      localStorage.removeItem(STORAGE_KEYS.SCOPE);
    }
    if (version) {
      localStorage.setItem(STORAGE_KEYS.VERSION, version);
    } else {
      localStorage.removeItem(STORAGE_KEYS.VERSION);
    }
  } catch (error) {
    console.error("Failed to save theme params to localStorage:", error);
  }
};

/**
 * Get theme parameters from localStorage
 */
export const getThemeParams = (): ThemeStorageParams => {
  try {
    return {
      themeId: localStorage.getItem(STORAGE_KEYS.THEME_ID),
      editMode: localStorage.getItem(STORAGE_KEYS.EDIT_MODE) === "true",
      editThemeToken: localStorage.getItem(STORAGE_KEYS.EDIT_THEME_TOKEN),
      previewMode: localStorage.getItem(STORAGE_KEYS.PREVIEW_MODE) === "true",
      appId: localStorage.getItem(STORAGE_KEYS.APP_ID),
      scope: localStorage.getItem(STORAGE_KEYS.SCOPE),
      version: localStorage.getItem(STORAGE_KEYS.VERSION),
    };
  } catch (error) {
    console.error("Failed to read theme params from localStorage:", error);
    return {
      themeId: null,
      editMode: false,
      editThemeToken: null,
      previewMode: false,
      appId: null,
      scope: null,
      version: null,
    };
  }
};

/**
 * Clear all theme parameters from localStorage
 */
export const clearThemeParams = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.THEME_ID);
    localStorage.removeItem(STORAGE_KEYS.EDIT_MODE);
    localStorage.removeItem(STORAGE_KEYS.EDIT_THEME_TOKEN);
    localStorage.removeItem(STORAGE_KEYS.PREVIEW_MODE);
    localStorage.removeItem(STORAGE_KEYS.APP_ID);
    localStorage.removeItem(STORAGE_KEYS.SCOPE);
    localStorage.removeItem(STORAGE_KEYS.VERSION);
  } catch (error) {
    console.error("Failed to clear theme params from localStorage:", error);
  }
};

/**
 * Sync URL parameters to localStorage. Triggers when either a V1
 * `themeId` or a V2 (`appId`+`scope`) target is present in the URL; the
 * two shapes share the same storage bucket but don't overwrite each
 * other — whichever shape is active on this page load is persisted.
 */
export const syncUrlToStorage = (): void => {
  try {
    const params = new URLSearchParams(window.location.search);
    const themeId = params.get("themeId");
    const editMode = params.get("editMode") === "true";
    const editThemeToken = params.get("editThemeToken");
    const previewMode = params.get("previewMode") === "true";
    const appId = params.get("appId");
    const scope = params.get("scope");
    const version = params.get("version");

    if (themeId || (appId && scope)) {
      saveThemeParams(themeId, editMode, editThemeToken, previewMode, appId, scope, version);
    }
  } catch (error) {
    console.error("Failed to sync URL to storage:", error);
  }
};

/**
 * Clear theme parameters from URL without reload
 */
export const clearUrlParams = (): void => {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete("themeId");
    url.searchParams.delete("editMode");
    url.searchParams.delete("editThemeToken");
    url.searchParams.delete("previewMode");
    url.searchParams.delete("origin");
    url.searchParams.delete("appId");
    url.searchParams.delete("scope");
    url.searchParams.delete("version");
    url.searchParams.delete("brandapp-v2");
    window.history.replaceState({}, "", url.toString());
  } catch (error) {
    console.error("Failed to clear URL params:", error);
  }
};

/**
 * Update URL parameters with current values
 */
export const updateUrlParams = (
  themeId: string | null,
  editMode: boolean,
  editThemeToken: string | null
): void => {
  try {
    const url = new URL(window.location.href);

    if (themeId) {
      url.searchParams.set("themeId", themeId);
      url.searchParams.set("editMode", String(editMode));
      if (editThemeToken) {
        url.searchParams.set("editThemeToken", editThemeToken);
      } else {
        url.searchParams.delete("editThemeToken");
      }
    } else {
      url.searchParams.delete("themeId");
      url.searchParams.delete("editMode");
      url.searchParams.delete("editThemeToken");
    }

    window.history.replaceState({}, "", url.toString());
  } catch (error) {
    console.error("Failed to update URL params:", error);
  }
};
